import React, { useMemo, useRef } from 'react'
import { useParams, Redirect } from 'react-router-dom'
import { useQuery } from 'jsonapi-react'
import { Helmet } from 'react-helmet-async'
import { Layout } from 'app/theme/layout'
import NotFoundPage from 'app/pages/NotFoundPage'
import ErrorPage from 'app/pages/ErrorPage'
import Hero from './profile/Hero'
import DetailsBar from './profile/DetailsBar'
import GoodFors from './profile/GoodFors'
import Quote from './profile/Quote'
import Menu from './profile/Menu'
import Packages from './profile/Packages'
import About from './profile/About'
import BookNextEvent from 'app/theme/banners/BookNextEvent'
import Events from './profile/Events'
import LeadModal from 'app/leads/LeadModal'
import NavBar from 'app/theme/NavBar'
import { useModalState } from 'lib/modal'
import { useInView } from 'react-intersection-observer'
import { useFeatureFlags } from 'app/utils/feature-flags'

export default function ProfilePage({ url, staticContext }) {
  const { catererSlug } = useParams()
  const [ flags ] = useFeatureFlags()

  if (staticContext) {
    staticContext.cacheMaxAge = 10800
  }

  const query = useQuery(['caterers', catererSlug, {
    include: [
      'story',
      'story.photos',
      'photos',
      'services',
      'tags',
      'good_fors',
      'menu_categories',
      'menu_categories.items',
      'menu_categories.items.photo',
      'cities',
      'cities.region',
      'cities.country',
    ],
  }])

  const { openModal, ...modalState } = useModalState()

  const [heroRef, heroInView] = useInView({
    rootMargin: '1px',
  })

  const [eventsRef, eventsInView] = useInView({
    rootMargin: '100px',
    triggerOnce: true,
  })

  const navbarRef = useRef()

  if (query.isLoading) {
    return null
  }

  if (query.error) {
    return <ErrorPage />
  }

  if (!query.data) {
    return <NotFoundPage />
  }

  if (query.data.slug !== catererSlug) {
    return <Redirect to={`/${query.data.slug}`} />
  }

  const caterer = query.data
  const city = caterer.cities[0]

  const preferred = (
    caterer.verified || (caterer.ranking_tier && caterer.ranking_tier <= 2)
  )

  const categories = caterer.menu_categories.filter(cat => cat.items.length > 0)

  const defaultMenuPhoto = caterer.photos.find(p => p.type === 'package_default')

  const allowInstantBooking = flags.instant_book && caterer.services.find(service => service.instant_book === true)

  const metaTitle = [
    caterer.name,
    caterer.cities.length ? caterer.cities[0].name : null,
    'Roaming Hunger'
  ].filter(Boolean).join(' - ')

  let metaDescription = (
    caterer.short_description ||
    caterer.description || ''
  ).substr(0, 197).replace(/\n/, ' ')

  if (metaDescription.length === 197) {
    metaDescription += '...'
  }

  const googleMetaTags = JSON.stringify({
    "@context": "https:\/\/schema.org",
    "@type": "Restaurant",
    "description": caterer.description,
    "image": caterer.photos[0]?.url,
    "name": caterer.name,
    "url": caterer.website,
  })

  return (
    <Layout>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription || caterer.name} />
        <meta name="og:title" content={metaTitle}></meta>
        <script type="application/ld+json">
          {googleMetaTags}
        </script>
      </Helmet>
      <div ref={heroRef}>
        <Hero
          caterer={caterer}
          photos={caterer.photos.filter(p => p.type !== 'menu_item')}
          preferred={preferred}
          showLeadForm={openModal}
          instantBooking={allowInstantBooking}
        />
      </div>
      <div ref={navbarRef} className="hidden lg:block fixed top-0 left-0 w-full z-50">
        {!heroInView && navbarRef.current && (
          <NavBar>
            <div className="flex justify-end">
              <button
                children="Book Catering"
                onClick={openModal}
                className={[
                  'btn text-white font-platform text-lg px-8 py-2',
                  'border-2 border-white'
                ]}
              />
            </div>
          </NavBar>
        )}
      </div>
      <DetailsBar
        caterer={caterer}
        city={city}
        showName={!preferred}
        showBox={!preferred}
      />
      {caterer.good_fors.length > 0 && (
        <GoodFors
          goodFors={caterer.good_fors}
        />
      )}
      {caterer.story?.quote && caterer.story.status !== 'disapproved' && (
        <Quote
          caterer={caterer}
          quote={caterer.story.quote}
          author={caterer.story.owner_name}
        />
      )}
      {categories.length > 0 && (
        <Menu
          categories={categories}
          itemPhotos={caterer.photos.filter(p => p.type === 'menu_item')}
          defaultPhoto={defaultMenuPhoto}
          hasQuote={!!caterer.story?.quote}
          className={caterer.good_fors.length || (caterer.story?.quote && caterer.story.status !== 'disapproved' ) ? null : 'pt-12'}
        />
      )}
      {/*
      {allowInstantBooking && (
        <Packages
          caterer={caterer}
        />
      )}
      */}
      {(caterer.description || (caterer.story && caterer.story.status !== 'disapproved' )) && (
        <About
          caterer={caterer}
          story={caterer.story}
        />
      )}
      <div ref={eventsRef} />
      {eventsInView && (
        <Events
          caterer={caterer}
        />
      )}
      <BookNextEvent
        onButtonClick={openModal}
      />
      <LeadModal
        initialData={{
          meta: {
            page_type: 'profile',
            caterer_id: caterer.id,
          }
        }}
        catererName={caterer.name}
        {...modalState}
      />
    </Layout>
  )
}
